import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import ContentContainer from "../../templates/content-container/contentContainer";
import { PAGE_IDS } from "../../app/data/common/pageIds";
import Button, { ButtonSizes, ButtonThemes } from "../../ui-components/button/button";
import PaymentService from "../../app/data/payment/paymentService";
import {
  AccountSurchargeApplication,
  AccountSurchargeWaiver,
  AchBankDetails,
  PaymentMethodSettings,
} from "../../app/data/payment/PaymentSession";
import AccountService from "../../app/data/account/accountService";
import LabeledSelectInput from "../../ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import { LabelModes } from "../../ui-components/molecules/labeled-inputs/labeledInput";
import { XGSSelectOption } from "../../ui-components/xgs-select/xgsSelect";
import { userSelector } from "../../slices/user/userSlice";
import { UserUtils } from "../../app/data/user/userUtils";
import XGSErrorMessage from "../../ui-components/error-message/errorMessage";
import XGSIcons from "../../ui-components/icon/xgsIcons";
import XGSIcon from "../../ui-components/icon/xgsIcon";
import XGSToggle from "../../ui-components/xgs-toggle/xgs-toggle";
import PaymentSettingsChangeLog from "./paymentSettingsChangeLog";
import "./paymentSettings.scss";

const paymentService = PaymentService.getInstance();
const accountService = AccountService.getInstance();
const WARNING_MAX = 500;
const DISPLAY_MESSAGE_MAX = 500;

const EMPTY_ACH: AchBankDetails = {
  bankName: "",
  accountName: "",
  routingNumber: "",
  accountNumber: "",
  accountType: "Checking",
};

const DEFAULT_METHOD_SETTINGS: PaymentMethodSettings = {
  ccEnabled: true,
  echeckEnabled: true,
  achEnabled: true,
  ccDisplayMessage: "",
  echeckDisplayMessage: "",
  achDisplayMessage: "",
  recommendedMethod: "",
};

const RECOMMENDED_OPTIONS: XGSSelectOption[] = [
  { label: "None", value: "" },
  { label: "Credit Card", value: "CC" },
  { label: "eCheck", value: "CK" },
  { label: "ACH / Wire", value: "ACH" },
];

const PaymentSettings: React.FC = () => {
  const userState = useSelector(userSelector);
  const [message, setMessage] = useState("");
  const [savedMessage, setSavedMessage] = useState("");
  const [surchargeEnabled, setSurchargeEnabled] = useState(true);
  const [savedSurchargeEnabled, setSavedSurchargeEnabled] = useState(true);
  const [methodSettings, setMethodSettings] = useState<PaymentMethodSettings>(DEFAULT_METHOD_SETTINGS);
  const [savedMethodSettings, setSavedMethodSettings] =
    useState<PaymentMethodSettings>(DEFAULT_METHOD_SETTINGS);
  const [achDetails, setAchDetails] = useState<AchBankDetails>(EMPTY_ACH);
  const [savedAch, setSavedAch] = useState<AchBankDetails>(EMPTY_ACH);
  const [loading, setLoading] = useState(false);
  const [savingWarning, setSavingWarning] = useState(false);
  const [savingSurchargeEnabled, setSavingSurchargeEnabled] = useState(false);
  const [savingMethods, setSavingMethods] = useState(false);
  const [savingAch, setSavingAch] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showChangeLog, setShowChangeLog] = useState(false);
  const [waivers, setWaivers] = useState<AccountSurchargeWaiver[]>([]);
  const [applications, setApplications] = useState<AccountSurchargeApplication[]>([]);
  const [savingWaiver, setSavingWaiver] = useState(false);
  const [savingApplication, setSavingApplication] = useState(false);

  const [waiverAccountSearch, setWaiverAccountSearch] = useState("");
  const [debouncedWaiverSearch, setDebouncedWaiverSearch] = useState("");
  const [waiverAccountOptions, setWaiverAccountOptions] = useState<XGSSelectOption[]>([]);
  const [searchingWaiverAccounts, setSearchingWaiverAccounts] = useState(false);
  const [selectedWaiverAccount, setSelectedWaiverAccount] = useState<XGSSelectOption | null>(null);

  const [applicationAccountSearch, setApplicationAccountSearch] = useState("");
  const [debouncedApplicationSearch, setDebouncedApplicationSearch] = useState("");
  const [applicationAccountOptions, setApplicationAccountOptions] = useState<XGSSelectOption[]>([]);
  const [searchingApplicationAccounts, setSearchingApplicationAccounts] = useState(false);
  const [selectedApplicationAccount, setSelectedApplicationAccount] =
    useState<XGSSelectOption | null>(null);

  const waivedAccountIds = useMemo(
    () => new Set(waivers.map((w) => w.accountNumber)),
    [waivers]
  );

  const applicationAccountIds = useMemo(
    () => new Set(applications.map((a) => a.accountNumber)),
    [applications]
  );

  const warningDirty = message.trim() !== savedMessage.trim();
  const methodsDirty =
    methodSettings.ccEnabled !== savedMethodSettings.ccEnabled ||
    methodSettings.echeckEnabled !== savedMethodSettings.echeckEnabled ||
    methodSettings.achEnabled !== savedMethodSettings.achEnabled ||
    methodSettings.ccDisplayMessage !== savedMethodSettings.ccDisplayMessage ||
    methodSettings.echeckDisplayMessage !== savedMethodSettings.echeckDisplayMessage ||
    methodSettings.achDisplayMessage !== savedMethodSettings.achDisplayMessage ||
    methodSettings.recommendedMethod !== savedMethodSettings.recommendedMethod;
  const achDirty =
    achDetails.bankName !== savedAch.bankName ||
    achDetails.accountName !== savedAch.accountName ||
    achDetails.routingNumber !== savedAch.routingNumber ||
    achDetails.accountNumber !== savedAch.accountNumber ||
    achDetails.accountType !== savedAch.accountType;

  const recommendedOptions = useMemo(() => {
    return RECOMMENDED_OPTIONS.filter((option) => {
      if (!option.value) return true;
      if (option.value === "CC") return methodSettings.ccEnabled;
      if (option.value === "CK") return methodSettings.echeckEnabled;
      if (option.value === "ACH") return methodSettings.achEnabled;
      return true;
    });
  }, [methodSettings.ccEnabled, methodSettings.echeckEnabled, methodSettings.achEnabled]);

  const recommendedValue =
    recommendedOptions.find((o) => o.value === methodSettings.recommendedMethod) ||
    recommendedOptions[0];

  useEffect(() => {
    setLoading(true);
    Promise.all([
      paymentService.getSurchargeWarning(),
      paymentService.getAchBankDetails(),
      paymentService.listAccountSurchargeWaivers(),
      paymentService.getPaymentMethodSettings(),
      paymentService.listAccountSurchargeApplications(),
    ]).then(([warningRes, achRes, waiverRes, methodsRes, applicationsRes]) => {
      if (warningRes.ok()) {
        const msg = warningRes.data?.message || "";
        const enabled = warningRes.data?.enabled !== false;
        setMessage(msg);
        setSavedMessage(msg);
        setSurchargeEnabled(enabled);
        setSavedSurchargeEnabled(enabled);
      } else {
        const err = warningRes.getError?.();
        setError(typeof err === "string" ? err : "Failed to load warning");
      }
      if (achRes.ok() && achRes.data) {
        const details = { ...EMPTY_ACH, ...achRes.data };
        setAchDetails(details);
        setSavedAch(details);
      } else if (!achRes.ok()) {
        const err = achRes.getError?.();
        setError(typeof err === "string" ? err : "Failed to load ACH bank details");
      }
      if (waiverRes.ok()) {
        setWaivers(waiverRes.data || []);
      }
      if (methodsRes.ok() && methodsRes.data) {
        const settings = { ...DEFAULT_METHOD_SETTINGS, ...methodsRes.data };
        setMethodSettings(settings);
        setSavedMethodSettings(settings);
      } else if (!methodsRes.ok()) {
        const err = methodsRes.getError?.();
        setError(typeof err === "string" ? err : "Failed to load payment method settings");
      }
      if (applicationsRes.ok()) {
        setApplications(applicationsRes.data || []);
      }
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedWaiverSearch(waiverAccountSearch), 300);
    return () => clearTimeout(handler);
  }, [waiverAccountSearch]);

  useEffect(() => {
    const handler = setTimeout(
      () => setDebouncedApplicationSearch(applicationAccountSearch),
      300
    );
    return () => clearTimeout(handler);
  }, [applicationAccountSearch]);

  useEffect(() => {
    if (!debouncedWaiverSearch.trim()) {
      setWaiverAccountOptions([]);
      return;
    }
    setSearchingWaiverAccounts(true);
    accountService.searchAccountsByNameOrNumber(debouncedWaiverSearch).then((res) => {
      if (res.ok() && res.data) {
        const options = res.data
          .filter((account) => account.accountName && !waivedAccountIds.has(account.accountId))
          .map((account) => ({
            label: `${account.accountName} (${account.accountId})`,
            value: String(account.accountId),
          }));
        setWaiverAccountOptions(options);
      } else {
        setWaiverAccountOptions([]);
      }
      setSearchingWaiverAccounts(false);
    });
  }, [debouncedWaiverSearch, waivedAccountIds]);

  useEffect(() => {
    if (!debouncedApplicationSearch.trim()) {
      setApplicationAccountOptions([]);
      return;
    }
    setSearchingApplicationAccounts(true);
    accountService.searchAccountsByNameOrNumber(debouncedApplicationSearch).then((res) => {
      if (res.ok() && res.data) {
        const options = res.data
          .filter(
            (account) => account.accountName && !applicationAccountIds.has(account.accountId)
          )
          .map((account) => ({
            label: `${account.accountName} (${account.accountId})`,
            value: String(account.accountId),
          }));
        setApplicationAccountOptions(options);
      } else {
        setApplicationAccountOptions([]);
      }
      setSearchingApplicationAccounts(false);
    });
  }, [debouncedApplicationSearch, applicationAccountIds]);

  const updateMethodField = <K extends keyof PaymentMethodSettings>(
    field: K,
    value: PaymentMethodSettings[K]
  ) => {
    setMethodSettings((prev) => {
      const next = { ...prev, [field]: value };
      if (
        field === "ccEnabled" &&
        !value &&
        next.recommendedMethod === "CC"
      ) {
        next.recommendedMethod = "";
      }
      if (
        field === "echeckEnabled" &&
        !value &&
        next.recommendedMethod === "CK"
      ) {
        next.recommendedMethod = "";
      }
      if (
        field === "achEnabled" &&
        !value &&
        next.recommendedMethod === "ACH"
      ) {
        next.recommendedMethod = "";
      }
      return next;
    });
  };

  const onSaveMethods = async () => {
    setSavingMethods(true);
    setError(null);
    const res = await paymentService.updatePaymentMethodSettings(methodSettings);
    if (res.ok() && res.data) {
      const settings = { ...DEFAULT_METHOD_SETTINGS, ...res.data };
      setMethodSettings(settings);
      setSavedMethodSettings(settings);
    } else {
      const err = res.getError?.();
      setError(typeof err === "string" ? err : "Failed to save payment method settings");
    }
    setSavingMethods(false);
  };

  const onAddWaiver = async () => {
    if (!selectedWaiverAccount) {
      return;
    }
    const accountNumber = parseInt(selectedWaiverAccount.value, 10);
    if (!accountNumber) {
      return;
    }
    setSavingWaiver(true);
    setError(null);
    const res = await paymentService.addAccountSurchargeWaiver(accountNumber);
    if (res.ok() && res.data) {
      setWaivers((prev) =>
        [...prev.filter((w) => w.accountNumber !== accountNumber), res.data!].sort(
          (a, b) => a.accountNumber - b.accountNumber
        )
      );
      setSelectedWaiverAccount(null);
      setWaiverAccountSearch("");
      setWaiverAccountOptions([]);
    } else {
      const err = res.getError?.();
      setError(typeof err === "string" ? err : "Failed to add account waiver");
    }
    setSavingWaiver(false);
  };

  const onRemoveWaiver = async (accountNumber: number) => {
    setSavingWaiver(true);
    setError(null);
    const res = await paymentService.removeAccountSurchargeWaiver(accountNumber);
    if (res.ok()) {
      setWaivers((prev) => prev.filter((w) => w.accountNumber !== accountNumber));
    } else {
      const err = res.getError?.();
      setError(typeof err === "string" ? err : "Failed to remove account waiver");
    }
    setSavingWaiver(false);
  };

  const onAddApplication = async () => {
    if (!selectedApplicationAccount) {
      return;
    }
    const accountNumber = parseInt(selectedApplicationAccount.value, 10);
    if (!accountNumber) {
      return;
    }
    setSavingApplication(true);
    setError(null);
    const res = await paymentService.addAccountSurchargeApplication(accountNumber);
    if (res.ok() && res.data) {
      setApplications((prev) =>
        [...prev.filter((a) => a.accountNumber !== accountNumber), res.data!].sort(
          (a, b) => a.accountNumber - b.accountNumber
        )
      );
      setSelectedApplicationAccount(null);
      setApplicationAccountSearch("");
      setApplicationAccountOptions([]);
    } else {
      const err = res.getError?.();
      setError(typeof err === "string" ? err : "Failed to add surcharge application");
    }
    setSavingApplication(false);
  };

  const onRemoveApplication = async (accountNumber: number) => {
    setSavingApplication(true);
    setError(null);
    const res = await paymentService.removeAccountSurchargeApplication(accountNumber);
    if (res.ok()) {
      setApplications((prev) => prev.filter((a) => a.accountNumber !== accountNumber));
    } else {
      const err = res.getError?.();
      setError(typeof err === "string" ? err : "Failed to remove surcharge application");
    }
    setSavingApplication(false);
  };

  const onSaveWarning = async () => {
    setSavingWarning(true);
    setError(null);
    const res = await paymentService.updateSurchargeWarning({ message });
    if (res.ok()) {
      const msg = res.data?.message || message;
      const enabled = res.data?.enabled !== false;
      setMessage(msg);
      setSavedMessage(msg);
      setSurchargeEnabled(enabled);
      setSavedSurchargeEnabled(enabled);
    } else {
      const err = res.getError?.();
      setError(typeof err === "string" ? err : "Failed to save surcharge warning");
    }
    setSavingWarning(false);
  };

  const onToggleSurchargeEnabled = async (enabled: boolean) => {
    setSavingSurchargeEnabled(true);
    setError(null);
    setSurchargeEnabled(enabled);
    const res = await paymentService.updateCcSurchargeEnabled(enabled);
    if (res.ok()) {
      const nextEnabled = res.data?.enabled !== false;
      setSurchargeEnabled(nextEnabled);
      setSavedSurchargeEnabled(nextEnabled);
      if (res.data?.message) {
        setMessage(res.data.message);
        setSavedMessage(res.data.message);
      }
    } else {
      setSurchargeEnabled(savedSurchargeEnabled);
      const err = res.getError?.();
      setError(typeof err === "string" ? err : "Failed to update surcharge setting");
    }
    setSavingSurchargeEnabled(false);
  };

  const onSaveAch = async () => {
    setSavingAch(true);
    setError(null);
    const res = await paymentService.updateAchBankDetails(achDetails);
    if (res.ok() && res.data) {
      const details = { ...EMPTY_ACH, ...res.data };
      setAchDetails(details);
      setSavedAch(details);
    } else {
      const err = res.getError?.();
      setError(typeof err === "string" ? err : "Failed to save ACH bank details");
    }
    setSavingAch(false);
  };

  const updateAchField = (field: keyof AchBankDetails, value: string) => {
    setAchDetails((prev) => ({ ...prev, [field]: value }));
  };

  const renderAccountTable = (
    rows: AccountSurchargeWaiver[],
    emptyText: string,
    onRemove: (accountNumber: number) => void,
    removing: boolean
  ) => (
    <div className="xgs-payment-settings__waiver-table-wrap">
      {rows.length === 0 ? (
        <p className="xgs-payment-settings__hint">{emptyText}</p>
      ) : (
        <table className="xgs-payment-settings__waiver-table">
          <thead>
            <tr>
              <th>Account</th>
              <th>Name</th>
              <th>Updated</th>
              <th>By</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.accountNumber}>
                <td>{row.accountNumber}</td>
                <td>{row.accountName || "—"}</td>
                <td>
                  {row.dateUpdated
                    ? row.dateUpdated.toUiDateTimeFormat?.() ?? row.dateUpdated
                    : "—"}
                </td>
                <td>{row.userUpdated || "—"}</td>
                <td>
                  <Button
                    theme={ButtonThemes.gray}
                    size={ButtonSizes.auto}
                    onClick={() => onRemove(row.accountNumber)}
                    disabled={removing}
                  >
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  if (!UserUtils.canAccessFinancePayments(userState.profile)) {
    return (
      <ContentContainer pageId={PAGE_IDS.PAYMENT_SETTINGS} title="Payment Settings">
        <XGSErrorMessage>Only XGS administrators and Finance Admins can edit payment settings.</XGSErrorMessage>
      </ContentContainer>
    );
  }

  return (
    <ContentContainer
      pageId={PAGE_IDS.PAYMENT_SETTINGS}
      title="Payment Settings"
      isLoading={loading}
      className="xgs-payment-settings-page"
    >
      <div className="xgs-payment-settings">
        <div className="xgs-payment-settings__intro">
          <p className="xgs-payment-settings__intro-text">
            Configure payment methods and messaging shown to customers during Invoice Payments
          </p>
          <Button
            theme={ButtonThemes.gray}
            size={ButtonSizes.auto}
            onClick={() => setShowChangeLog(true)}
          >
            <XGSIcon icon={XGSIcons.faFileAlt} /> View change log
          </Button>
        </div>

        {error && <XGSErrorMessage>{error}</XGSErrorMessage>}

        <section className="xgs-payment-settings__card">
          <div className="xgs-payment-settings__card-header">
            <div className="xgs-payment-settings__card-heading">
              <span className="xgs-payment-settings__card-icon xgs-payment-settings__card-icon--methods">
                <XGSIcon icon={XGSIcons.faSlidersH} />
              </span>
              <div>
                <h3>Payment Methods</h3>
                <p>
                  Control which methods appear in the payment selector, optional messages, and the
                  recommended default
                </p>
              </div>
            </div>
            {!methodsDirty && (
              <span className="xgs-payment-settings__status">
                <span className="xgs-payment-settings__status-dot" />
                Saved
              </span>
            )}
          </div>

          <div className="xgs-payment-settings__method-toggles">
            <XGSToggle
              label="Credit Card"
              checked={methodSettings.ccEnabled}
              onChange={(value) => updateMethodField("ccEnabled", value)}
              disabled={savingMethods}
            />
            <XGSToggle
              label="eCheck"
              checked={methodSettings.echeckEnabled}
              onChange={(value) => updateMethodField("echeckEnabled", value)}
              disabled={savingMethods}
            />
            <XGSToggle
              label="ACH / Wire"
              checked={methodSettings.achEnabled}
              onChange={(value) => updateMethodField("achEnabled", value)}
              disabled={savingMethods}
            />
          </div>

          <div className="xgs-payment-settings__method-messages">
            <div className="xgs-payment-settings__field-block">
              <label className="xgs-payment-settings__label" htmlFor="cc-display-message">
                Credit Card display message
              </label>
              <textarea
                id="cc-display-message"
                className="xgs-payment-settings__textarea"
                value={methodSettings.ccDisplayMessage}
                onChange={(e) => updateMethodField("ccDisplayMessage", e.target.value)}
                maxLength={DISPLAY_MESSAGE_MAX}
                rows={4}
                disabled={!methodSettings.ccEnabled}
              />
            </div>
            <div className="xgs-payment-settings__field-block">
              <label className="xgs-payment-settings__label" htmlFor="echeck-display-message">
                eCheck display message
              </label>
              <textarea
                id="echeck-display-message"
                className="xgs-payment-settings__textarea"
                value={methodSettings.echeckDisplayMessage}
                onChange={(e) => updateMethodField("echeckDisplayMessage", e.target.value)}
                maxLength={DISPLAY_MESSAGE_MAX}
                rows={4}
                disabled={!methodSettings.echeckEnabled}
              />
            </div>
            <div className="xgs-payment-settings__field-block">
              <label className="xgs-payment-settings__label" htmlFor="ach-display-message">
                ACH / Wire display message
              </label>
              <textarea
                id="ach-display-message"
                className="xgs-payment-settings__textarea"
                value={methodSettings.achDisplayMessage}
                onChange={(e) => updateMethodField("achDisplayMessage", e.target.value)}
                maxLength={DISPLAY_MESSAGE_MAX}
                rows={4}
                disabled={!methodSettings.achEnabled}
              />
            </div>
          </div>

          <div className="xgs-payment-settings__recommended">
            <LabeledSelectInput
              label="Recommended method"
              labelMode={LabelModes.column}
              placeholder="None"
              isClearable
              options={recommendedOptions}
              value={recommendedValue.value ? recommendedValue : null}
              onValueChange={(option) =>
                updateMethodField("recommendedMethod", option?.value || "")
              }
            />
            <p className="xgs-payment-settings__hint">
              Shown with a Recommended badge in the payment selector. Only enabled methods can be
              selected.
            </p>
          </div>

          <div className="xgs-payment-settings__card-footer">
            <Button
              theme={ButtonThemes.blue}
              size={ButtonSizes.auto}
              onClick={onSaveMethods}
              disabled={savingMethods || !methodsDirty}
              spinner={savingMethods}
            >
              <XGSIcon icon={XGSIcons.faSave} />{" "}
              {methodsDirty ? "Save method settings" : "Saved"}
            </Button>
          </div>
        </section>

        <section className="xgs-payment-settings__card">
          <div className="xgs-payment-settings__card-header">
            <div className="xgs-payment-settings__card-heading">
              <span className="xgs-payment-settings__card-icon xgs-payment-settings__card-icon--warn">
                <XGSIcon icon={XGSIcons.faExclamationTriangle} />
              </span>
              <div>
                <h3>Credit Card Surcharge</h3>
                <p>Configure Interpayments vs Authorize.Net credit card routing and fee messaging</p>
              </div>
            </div>
            {!warningDirty && !!savedMessage && surchargeEnabled && (
              <span className="xgs-payment-settings__status">
                <span className="xgs-payment-settings__status-dot" />
                Saved
              </span>
            )}
          </div>

          <div className="xgs-payment-settings__toggle-row">
            <XGSToggle
              label="Apply credit card surcharge (Interpayments)"
              checked={surchargeEnabled}
              onChange={onToggleSurchargeEnabled}
              disabled={savingSurchargeEnabled}
            />
            <p className="xgs-payment-settings__hint">
              {surchargeEnabled
                ? "When ON, payors use Interpayments with surcharge by default. Accounts on the waiver list use Authorize.Net CC without surcharge."
                : "When OFF, payors use Authorize.Net CC without surcharge by default. Accounts on the surcharge application list use Interpayments with surcharge."}
            </p>
          </div>

          <div className="xgs-payment-settings__warning-grid">
            <div className="xgs-payment-settings__field-block">
              <label className="xgs-payment-settings__label" htmlFor="cc-warning-message">
                Warning message
              </label>
              {!surchargeEnabled && (
                <p className="xgs-payment-settings__hint">
                  Surcharge warning is hidden while surcharge is disabled (except for application-list
                  payors).
                </p>
              )}
              <textarea
                id="cc-warning-message"
                className="xgs-payment-settings__textarea"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={WARNING_MAX}
                rows={5}
                disabled={!surchargeEnabled}
              />
              <div className="xgs-payment-settings__char-count">
                {message.length} characters
              </div>
            </div>

            <div className="xgs-payment-settings__field-block">
              <div className="xgs-payment-settings__label">Customer preview</div>
              <div className="xgs-payment-settings__preview">
                <XGSIcon icon={XGSIcons.faInfoCircle} />
                <span>
                  {message.trim() ||
                    "Please note credit card payments may have additional charges applied."}
                </span>
              </div>
              <div className="xgs-payment-settings__hint">
                This appears in the payment method picker after Credit Card is selected when a
                surcharge applies.
              </div>
            </div>
          </div>

          <div className="xgs-payment-settings__card-footer">
            <Button
              theme={ButtonThemes.blue}
              size={ButtonSizes.auto}
              onClick={onSaveWarning}
              disabled={savingWarning || !surchargeEnabled || !message.trim() || !warningDirty}
              spinner={savingWarning}
            >
              <XGSIcon icon={XGSIcons.faSave} />{" "}
              {warningDirty ? "Save warning" : "Saved"}
            </Button>
          </div>
        </section>

        <section
          className={
            "xgs-payment-settings__card" +
            (surchargeEnabled ? " xgs-payment-settings__card--emphasized" : "")
          }
        >
          <div className="xgs-payment-settings__card-header">
            <div className="xgs-payment-settings__card-heading">
              <span className="xgs-payment-settings__card-icon xgs-payment-settings__card-icon--ach">
                <XGSIcon icon={XGSIcons.faUsers} />
              </span>
              <div>
                <h3>Per-Account Surcharge Waivers</h3>
                <p>
                  When global surcharge is ON, these payors use Authorize.Net CC without surcharge
                </p>
              </div>
            </div>
          </div>

          <p className="xgs-payment-settings__hint">
            {surchargeEnabled
              ? "Currently active: waived accounts skip Interpayments and pay invoice total via Authorize.Net CC."
              : "Not currently applied — global surcharge is OFF. Waivers take effect when surcharge is turned ON."}
          </p>

          <div className="xgs-payment-settings__waiver-add">
            <LabeledSelectInput
              label="Add account waiver"
              labelMode={LabelModes.column}
              placeholder="Search by name or account number..."
              isClearable
              isLoading={searchingWaiverAccounts}
              onInputChange={(value) => setWaiverAccountSearch(value)}
              options={waiverAccountOptions}
              value={selectedWaiverAccount}
              onValueChange={(option) => {
                setSelectedWaiverAccount(option || null);
                if (!option) {
                  setWaiverAccountSearch("");
                  setWaiverAccountOptions([]);
                }
              }}
            />
            <Button
              theme={ButtonThemes.blue}
              size={ButtonSizes.auto}
              onClick={onAddWaiver}
              disabled={!selectedWaiverAccount || savingWaiver}
              spinner={savingWaiver}
            >
              <XGSIcon icon={XGSIcons.faPlus} /> Add waiver
            </Button>
          </div>

          {renderAccountTable(
            waivers,
            "No per-account waivers configured.",
            onRemoveWaiver,
            savingWaiver
          )}
        </section>

        <section
          className={
            "xgs-payment-settings__card" +
            (!surchargeEnabled ? " xgs-payment-settings__card--emphasized" : "")
          }
        >
          <div className="xgs-payment-settings__card-header">
            <div className="xgs-payment-settings__card-heading">
              <span className="xgs-payment-settings__card-icon xgs-payment-settings__card-icon--application">
                <XGSIcon icon={XGSIcons.faUserPlus} />
              </span>
              <div>
                <h3>Surcharge Application</h3>
                <p>
                  When global surcharge is OFF, these payors use Interpayments with surcharge
                </p>
              </div>
            </div>
          </div>

          <p className="xgs-payment-settings__hint">
            {!surchargeEnabled
              ? "Currently active: listed payors are routed to Interpayments with surcharge instead of Authorize.Net CC."
              : "Not currently applied — global surcharge is ON. Applications take effect when surcharge is turned OFF."}
          </p>

          <div className="xgs-payment-settings__waiver-add">
            <LabeledSelectInput
              label="Add surcharge application"
              labelMode={LabelModes.column}
              placeholder="Search by name or account number..."
              isClearable
              isLoading={searchingApplicationAccounts}
              onInputChange={(value) => setApplicationAccountSearch(value)}
              options={applicationAccountOptions}
              value={selectedApplicationAccount}
              onValueChange={(option) => {
                setSelectedApplicationAccount(option || null);
                if (!option) {
                  setApplicationAccountSearch("");
                  setApplicationAccountOptions([]);
                }
              }}
            />
            <Button
              theme={ButtonThemes.blue}
              size={ButtonSizes.auto}
              onClick={onAddApplication}
              disabled={!selectedApplicationAccount || savingApplication}
              spinner={savingApplication}
            >
              <XGSIcon icon={XGSIcons.faPlus} /> Add application
            </Button>
          </div>

          {renderAccountTable(
            applications,
            "No surcharge applications configured.",
            onRemoveApplication,
            savingApplication
          )}
        </section>

        <section className="xgs-payment-settings__card">
          <div className="xgs-payment-settings__card-header">
            <div className="xgs-payment-settings__card-heading">
              <span className="xgs-payment-settings__card-icon xgs-payment-settings__card-icon--ach">
                <XGSIcon icon={XGSIcons.faExchangeAlt} />
              </span>
              <div>
                <h3>ACH / Wire Bank Details</h3>
                <p>
                  Shown to customers when they select ACH / Wire for manual bank transfer
                </p>
              </div>
            </div>
            {!achDirty && (
              <span className="xgs-payment-settings__status">
                <span className="xgs-payment-settings__status-dot" />
                Saved
              </span>
            )}
          </div>

          <div className="xgs-payment-settings__info-banner">
            <XGSIcon icon={XGSIcons.faInfoCircle} />
            <span>
              The <strong>Reference / Memo</strong> field is automatically generated from
              the invoice numbers being paid — you don&apos;t need to configure it here.
            </span>
          </div>

          <div className="xgs-payment-settings__ach-grid">
            <label className="xgs-payment-settings__field">
              <span className="xgs-payment-settings__label">Bank name</span>
              <input
                type="text"
                value={achDetails.bankName}
                onChange={(e) => updateAchField("bankName", e.target.value)}
              />
            </label>
            <label className="xgs-payment-settings__field">
              <span className="xgs-payment-settings__label">Account name</span>
              <input
                type="text"
                value={achDetails.accountName}
                onChange={(e) => updateAchField("accountName", e.target.value)}
              />
            </label>
            <label className="xgs-payment-settings__field">
              <span className="xgs-payment-settings__label">Routing number (ABA)</span>
              <input
                type="text"
                value={achDetails.routingNumber}
                onChange={(e) => updateAchField("routingNumber", e.target.value)}
              />
            </label>
            <label className="xgs-payment-settings__field">
              <span className="xgs-payment-settings__label">Account number</span>
              <input
                type="text"
                value={achDetails.accountNumber}
                onChange={(e) => updateAchField("accountNumber", e.target.value)}
              />
            </label>
            <label className="xgs-payment-settings__field">
              <span className="xgs-payment-settings__label">Account type</span>
              <input
                type="text"
                value={achDetails.accountType}
                onChange={(e) => updateAchField("accountType", e.target.value)}
              />
            </label>
            <div className="xgs-payment-settings__field">
              <div className="xgs-payment-settings__label">
                Reference / Memo{" "}
                <span className="xgs-payment-settings__label-meta">Auto-generated</span>
              </div>
              <div className="xgs-payment-settings__readonly">
                <XGSIcon icon={XGSIcons.faLock} />
                <span>INV-{"{invoice_numbers}"}</span>
              </div>
            </div>
          </div>

          <div className="xgs-payment-settings__card-footer xgs-payment-settings__card-footer--split">
            <p className="xgs-payment-settings__footer-hint">
              These details will be shown to customers verbatim — double-check before saving.
            </p>
            <Button
              theme={ButtonThemes.blue}
              size={ButtonSizes.auto}
              onClick={onSaveAch}
              disabled={savingAch || !achDirty}
              spinner={savingAch}
            >
              <XGSIcon icon={XGSIcons.faSave} /> Save ACH Details
            </Button>
          </div>
        </section>
      </div>

      <PaymentSettingsChangeLog
        show={showChangeLog}
        onClose={() => setShowChangeLog(false)}
      />
    </ContentContainer>
  );
};

export default PaymentSettings;
